<script setup lang="ts">
import type { DefaultModelMode, ModelCardView } from '~/features/my-wallet/types/receiving-model'
import { I18nT, useI18n } from 'vue-i18n'
import ModelTag from '~/features/my-wallet/components/ModelTag.vue'
import ModelTitle from '~/features/my-wallet/components/ModelTitle.vue'
import StatusChip from '~/features/my-wallet/components/StatusChip.vue'
import DefaultModelConditions from './DefaultModelConditions.vue'
import ModelRequirement from './ModelRequirement.vue'

interface Props {
  card: ModelCardView
  mode: DefaultModelMode
}

const props = defineProps<Props>()

const emit = defineEmits<{
  seeDetails: []
  retry: []
}>()

const { t } = useI18n()

const isDefaultModel = computed(() => props.card.key === 'C')
const titleId = computed(() => `my-wallet-model-${props.card.key.toLowerCase()}-title`)

const DEFAULT_MODEL_LABEL_BY_MODE: Record<DefaultModelMode, string> = {
  default: 'my_wallet.receiving_model.card.requirements_label',
  novice: 'my_wallet.receiving_model.card.for_whom_label',
  essential: 'my_wallet.receiving_model.card.for_whom_label',
}

const milestone = computed(() => props.card.progress?.kind === 'milestone' ? props.card.progress : null)
const performanceLine = computed(() => props.card.progress?.kind === 'performance' ? props.card.progress : null)

const performanceValueText = computed(() => {
  const performanceLineView = performanceLine.value
  if (!performanceLineView)
    return ''
  if (performanceLineView.notEvaluated)
    return t('my_wallet.receiving_model.card.not_evaluated')
  if (performanceLineView.percentage === null)
    return t('my_wallet.receiving_model.card.unavailable')
  return t('my_wallet.receiving_model.card.performance_value', { percentage: performanceLineView.percentage })
})
</script>

<template>
  <article class="model-card" :class="{ '-inactive': !card.isActive }" :aria-labelledby="titleId">
    <header class="model-card-header">
      <div class="model-card-chips">
        <template v-if="mode === 'essential'">
          <StatusChip :id="`my-wallet-model-${card.key}-default-chip`" state="DEFAULT" />
          <StatusChip
            v-if="card.state !== 'DEFAULT'"
            :id="`my-wallet-model-${card.key}-chip`"
            :state="card.state"
          />
        </template>
        <template v-else>
          <ModelTag :model="card.key" :variant="card.tagVariant" />
          <StatusChip :id="`my-wallet-model-${card.key}-chip`" :state="card.state" />
        </template>
      </div>

      <div class="model-card-split">
        <h4 :id="titleId" class="model-card-title">
          <ModelTitle :model="card.key" />
        </h4>
        <div class="model-card-split-values">
          <p class="model-card-split-value">
            {{ t('my_wallet.models.split', { upfront: card.upfrontPercentage, after: card.afterCompletionPercentage }) }}
          </p>
          <p class="model-card-split-caption">
            {{ t('my_wallet.models.split_caption') }}
          </p>
        </div>
      </div>
    </header>

    <hr class="model-card-divider">

    <div class="model-card-body">
      <template v-if="isDefaultModel">
        <p class="model-card-label">
          {{ t(DEFAULT_MODEL_LABEL_BY_MODE[mode]) }}
        </p>
        <DefaultModelConditions
          class="model-card-conditions"
          :conditions="card.conditions"
          :mode="mode"
          :milestone="milestone"
          @retry="emit('retry')"
        />
      </template>

      <template v-else>
        <div class="model-card-steps-header">
          <p class="model-card-label">
            {{ t('my_wallet.receiving_model.card.requirements_label') }}
          </p>
          <p v-if="card.isWaived" class="model-card-waived-note">
            {{ t('my_wallet.receiving_model.card.steps_waived_note') }}
          </p>
        </div>
        <ul class="model-card-requirements">
          <ModelRequirement
            v-for="requirement in card.requirements"
            :key="requirement.code"
            :requirement="requirement"
            :model="card.key"
          />
        </ul>
      </template>

      <I18nT
        v-if="performanceLine"
        keypath="my_wallet.receiving_model.card.current_performance"
        tag="p"
        class="model-card-performance"
        :class="{ '-indented': !isDefaultModel, '-checklist': isDefaultModel && mode !== 'novice' }"
      >
        <template #value>
          <button
            v-if="performanceLine.linksToDetails"
            :id="`my-wallet-model-${card.key}-see-details`"
            type="button"
            class="model-card-performance-link"
            @click="emit('seeDetails')"
          >
            {{ performanceValueText }}
            <span class="sr-only">{{ t('my_wallet.receiving_model.card.performance_link_context', { model: card.key }) }}</span>
          </button>
          <strong v-else class="model-card-performance-value">{{ performanceValueText }}</strong>
        </template>
      </I18nT>
    </div>
  </article>
</template>

<style scoped lang="scss">
@use '../../styles/typography';

.model-card {
  @apply flex flex-col gap-8 w-full min-w-0 h-full p-6 rounded-lg;
  @apply bg-neutral-high-pure border border-neutral-high-medium;

  &.-inactive {
    @apply bg-neutral-high-light;

    .model-card-split,
    .model-card-label,
    .model-card-performance {
      @apply opacity-60;
    }

    .model-card-conditions > :deep(li:not(.default-model-error)) {
      @apply opacity-60;
    }

    .model-requirement:not(.-alert) {
      @apply opacity-60;
    }

    .model-card-split-caption {
      @apply text-brand-primary-light;
    }
  }

  .model-card-header {
    @apply flex flex-col gap-6 w-full;
  }

  .model-card-chips {
    @apply flex flex-wrap items-start gap-2;
  }

  .model-card-split {
    @apply flex flex-wrap items-center justify-between gap-2 w-full min-h-16;
  }

  .model-card-title {
    flex: 1 1 8rem;
  }

  .model-card-title,
  .model-card-split-value {
    @apply text-neutral-low-dark;
    @include typography.title-20;
  }

  .model-card-split-values {
    @apply flex flex-col items-end flex-shrink-0 ml-auto text-right;
    gap: 2px;
  }

  .model-card-split-caption {
    @apply fonts-body-medium-regular text-brand-primary-medium;
  }

  .model-card-divider {
    @apply w-full h-px m-0 border-0 bg-neutral-high-medium;
  }

  .model-card-body {
    @apply flex flex-col gap-6 w-full pb-2;
  }

  .model-card-steps-header {
    @apply flex flex-col gap-6 w-full;
  }

  .model-card-label {
    @apply fonts-body-small-bold uppercase text-neutral-low-light;
  }

  .model-card-waived-note {
    @apply w-full px-4 pt-3 pb-4 rounded-md bg-neutral-high-light;
    @apply fonts-body-medium-bold text-neutral-low-dark;
  }

  .model-card-requirements {
    @apply flex flex-col gap-6 w-full;
  }

  .model-card-performance {
    @apply w-full -mt-2 pl-5 fonts-body-medium-regular text-neutral-low-dark;

    &.-indented {
      @apply -mt-3 pl-[22px];
    }

    &.-checklist {
      @apply pl-[24px];
    }
  }

  .model-card-performance-value {
    @apply fonts-body-medium-bold;
  }

  .model-card-performance-link {
    @apply fonts-body-medium-bold text-brand-secondary-dark underline cursor-pointer;
    text-underline-offset: 2px;
  }
}
</style>
